/**
 * Centralized Profile Completion Hook
 * Can be reused across multiple screens to get profile completion data
 *
 * The underlying fetch (WhatsApp phones, Facebook pages, X accounts, phone
 * account, business users, omni-channel stats — up to 6 sequential network
 * calls) plus the token-change subscription and 2-minute auto-refresh are
 * all run through a single module-level singleton, not per call site. This
 * hook is called from several places that render unconditionally on nearly
 * every screen (LeftPane, RightPane) alongside the screens themselves and
 * ContactsList — before this, EACH call site ran its own independent copy of
 * the entire fetch sequence and its own independent 2-minute poll. Confirmed
 * via a real production request-log capture: facebook-messenger/pages,
 * business/:id/users, omni-channel/stats and whatsapp/phone-numbers/
 * registrations were each firing 2-3x on a single page load purely from
 * this hook being called multiple times on the same screen, not from any
 * genuine need for fresh data per caller — it's the exact same business/
 * token for every one of them.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAppStore } from '../store/appStore';
import { ProfileCompletionData, ServiceStatus } from '../components/ProfileCompletionWidget/ProfileCompletionCalculator';
import { decodeJWT, checkWhatsAppConfiguration, checkFacebookMessengerConfiguration, checkXTwitterConfiguration, checkPhoneConfiguration, checkVisitsConfiguration } from '../utils/token-utils';
import { apiService } from '../services/api';
import { WhatsAppPhoneCardData } from '../components/whatsapp/WhatsAppPhoneCard';

export interface BusinessStats {
  totalContacts: number;
  messagesThisMonth: number;
  teamCount: number;
}

export interface FacebookPage {
  id: string;
  page_id: string;
  page_name: string;
  status?: string;
  isVerified?: boolean;
}

export interface XTwitterAccount {
  xUserId: string;
  xUsername: string;
  displayName: string;
  status?: string;
  tokenExpired?: boolean;
}

export interface PhoneAccount {
  provider: string;
  callerNumber?: string;
  status: 'active' | 'inactive' | 'invalid';
}

interface ProfileCompletionState {
  profileCompletion: ProfileCompletionData;
  whatsappPhones: WhatsAppPhoneCardData[];
  facebookPages: FacebookPage[];
  xAccounts: XTwitterAccount[];
  phoneAccount: PhoneAccount | null;
  businessStats: BusinessStats | null;
  isLoading: boolean;
  error: string | null;
}

const DEFAULT_PROFILE_COMPLETION: ProfileCompletionData = {
  percentage: 0,
  totalServices: 0,
  completedServices: 0,
  services: []
};

let sharedState: ProfileCompletionState = {
  profileCompletion: DEFAULT_PROFILE_COMPLETION,
  whatsappPhones: [],
  facebookPages: [],
  xAccounts: [],
  phoneAccount: null,
  businessStats: null,
  isLoading: false,
  error: null,
};

const listeners = new Set<() => void>();

function setSharedState(patch: Partial<ProfileCompletionState>) {
  sharedState = { ...sharedState, ...patch };
  listeners.forEach(listener => listener());
}

// De-dupes concurrent calls the same way ContactsList's own filter-metadata
// caching does — several subscribers mounting at once (e.g. LeftPane +
// RightPane on the same screen) must share one in-flight fetch, not each
// kick off their own.
let fetchInFlight: Promise<void> | null = null;

async function fetchProfileCompletionShared(): Promise<void> {
  if (fetchInFlight) return fetchInFlight;

  fetchInFlight = (async () => {
    setSharedState({ isLoading: true, error: null });

    try {
      console.log('🔍 [useProfileCompletion] Fetching profile completion data from token...');

      const currentToken = useAppStore.getState().tokens?.token;

      if (!currentToken) {
        console.log('⚠️ [useProfileCompletion] No token available');
        setSharedState({ profileCompletion: DEFAULT_PROFILE_COMPLETION });
        return;
      }

      const decoded = decodeJWT(currentToken);
      const availableServices = decoded?.subscription?.services || {};
      const hasWhatsAppService = !!availableServices?.wabu;
      const hasFacebookService = !!availableServices?.fbm;
      const hasXService = !!availableServices?.x_twitter;
      const hasPhoneService = !!availableServices?.phone;
      const hasVisitsService = !!availableServices?.visits;
      const hasAiService = !!availableServices?.ai;
      let aiConfigured = hasAiService && availableServices.ai?.status === 'active' && !!availableServices.ai?.lastTestedAt;
      if (hasAiService) {
        try {
          const response = await apiService.getAiStatus();
          if (response.statusCode === 1000 && response.data) aiConfigured = response.data.configured;
        } catch { /* Keep the token snapshot when the API is unavailable. */ }
      }

      // A user who registered with only phone OR only email is missing the other verified
      // identity — surface it as an incomplete profile step so they can add/verify it later
      // (tapping it launches the full OTP attach-and-verify flow, never an unverified form,
      // so isEmailVerified/isMobileVerified always reflect genuine proof of ownership).
      const emailVerified = !!decoded?.emailVerified;
      const mobileVerified = !!decoded?.mobileVerified;
      const missingEmailIdentity = mobileVerified && !emailVerified;
      const missingMobileIdentity = emailVerified && !mobileVerified;

      // Only fetch connected phone numbers when the business actually has
      // the WhatsApp service — avoids a wasted network call otherwise.
      // Live lists (null = the call failed): what's connected right now,
      // whereas the token's copy is only as fresh as the last login/refresh.
      let livePhones: WhatsAppPhoneCardData[] | null = null;
      let livePages: FacebookPage[] | null = null;
      let liveXAccounts: XTwitterAccount[] | null = null;
      let livePhoneAccount: PhoneAccount | null | undefined;
      if (hasWhatsAppService) {
        try {
          const phonesResponse = await apiService.getWhatsAppPhoneNumbers();
          livePhones = Array.isArray(phonesResponse.data) ? phonesResponse.data : [];
          setSharedState({ whatsappPhones: livePhones });
        } catch (phoneError) {
          console.log('⚠️ [useProfileCompletion] Could not fetch WhatsApp phone numbers:', phoneError);
          setSharedState({ whatsappPhones: [] });
        }
      } else {
        setSharedState({ whatsappPhones: [] });
      }

      // Same pattern for Facebook Messenger connected pages — gated on
      // service presence to avoid a wasted call.
      if (hasFacebookService) {
        try {
          const pagesResponse = await apiService.getFacebookPages();
          livePages = Array.isArray(pagesResponse.data) ? pagesResponse.data : [];
          setSharedState({ facebookPages: livePages });
        } catch (pagesError) {
          console.log('⚠️ [useProfileCompletion] Could not fetch Facebook pages:', pagesError);
          setSharedState({ facebookPages: [] });
        }
      } else {
        setSharedState({ facebookPages: [] });
      }

      // Same pattern for X (Twitter) connected accounts.
      if (hasXService) {
        try {
          const accountsResponse = await apiService.getXTwitterAccounts();
          liveXAccounts = Array.isArray(accountsResponse.data) ? accountsResponse.data : [];
          setSharedState({ xAccounts: liveXAccounts });
        } catch (accountsError) {
          console.log('⚠️ [useProfileCompletion] Could not fetch X Twitter accounts:', accountsError);
          setSharedState({ xAccounts: [] });
        }
      } else {
        setSharedState({ xAccounts: [] });
      }

      // Same pattern for the phone channel's single connected account —
      // gated on service presence to avoid a wasted call.
      if (hasPhoneService) {
        try {
          const accountResponse = await apiService.getPhoneAccount();
          livePhoneAccount = accountResponse.data ?? null;
          setSharedState({ phoneAccount: livePhoneAccount });
        } catch (accountError) {
          console.log('⚠️ [useProfileCompletion] Could not fetch phone account:', accountError);
          setSharedState({ phoneAccount: null });
        }
      } else {
        setSharedState({ phoneAccount: null });
      }

      // Check WhatsApp configuration from token
      const whatsappConfig = checkWhatsAppConfiguration(currentToken);
      console.log('🔍 [useProfileCompletion] WhatsApp config from token:', whatsappConfig);

      // Check Facebook Messenger configuration from token
      const facebookConfig = checkFacebookMessengerConfiguration(currentToken);
      console.log('🔍 [useProfileCompletion] Facebook Messenger config from token:', facebookConfig);

      // Check X Twitter configuration from token
      const xConfig = checkXTwitterConfiguration(currentToken);
      console.log('🔍 [useProfileCompletion] X Twitter config from token:', xConfig);

      // Check phone channel configuration from token
      const phoneConfig = checkPhoneConfiguration(currentToken);
      console.log('🔍 [useProfileCompletion] Phone config from token:', phoneConfig);

      // Check Visits configuration from token
      const visitsConfig = checkVisitsConfiguration(currentToken);
      console.log('🔍 [useProfileCompletion] Visits config from token:', visitsConfig);

      // Calculate completion based on available services only
      // Live data decides; the token is the fallback when a call failed.
      const isLive = (status?: string) => ['verified', 'active', 'connected'].includes(String(status || '').toLowerCase());
      const whatsappConfigured = hasWhatsAppService && (livePhones
        ? livePhones.some((p) => p.isVerified || isLive(p.status))
        : whatsappConfig.hasMetadata && whatsappConfig.verifiedPhones > 0);
      const facebookConfigured = hasFacebookService && (livePages
        ? livePages.some((p) => p.isVerified || isLive(p.status))
        : facebookConfig.hasMetadata && facebookConfig.verifiedPages > 0);
      const xConfigured = hasXService && (liveXAccounts
        ? liveXAccounts.some((a) => !a.tokenExpired && (a.status === undefined || isLive(a.status)))
        : xConfig.hasMetadata && xConfig.activeAccounts > 0);
      const phoneConfigured = hasPhoneService && (livePhoneAccount !== undefined
        ? livePhoneAccount?.status === 'active'
        : phoneConfig.isConfigured && phoneConfig.isActive);
      const visitsConfigured = hasVisitsService && visitsConfig.isConfigured && visitsConfig.isActive;

      // Business users are fetched once, unconditionally (all roles need
      // the team count for businessStats below) — the owner-only "is user
      // management configured" check reuses the same fetched array
      // instead of issuing its own separate, role-gated request.
      let userManagementConfigured = false;
      const businessRole = useAppStore.getState().businessRole;
      const currentBusiness = useAppStore.getState().currentBusiness;

      let businessUsers: any[] = [];
      if (currentBusiness?._id) {
        try {
          const usersResponse = await apiService.getBusinessUsers(currentBusiness._id);
          businessUsers = Array.isArray(usersResponse.data) ? usersResponse.data : [];
        } catch (usersError) {
          console.log('⚠️ [useProfileCompletion] Could not fetch business users:', usersError);
          businessUsers = [];
        }
      }

      if (businessRole === 'owner') {
        const currentUserId = useAppStore.getState().user?.id;
        if (currentUserId) {
          const otherUsers = businessUsers.filter((businessUser: any) => {
            const userId = businessUser.userId || businessUser.user?.id || businessUser.user?._id;
            return userId && userId.toString() !== currentUserId.toString();
          });
          userManagementConfigured = otherUsers.length > 0;
        }
      } else {
        // For non-owners, skip user management check entirely
        userManagementConfigured = true; // Consider it complete for non-owners
      }

      // Business-wide stats (contacts, messages this month, team size) —
      // role-independent, always fetched, shown as a compact strip on the
      // widget regardless of which service the carousel is showing.
      try {
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        startOfMonth.setHours(0, 0, 0, 0);
        const statsResponse = await apiService.getOmniChannelStats(startOfMonth);
        if (statsResponse.data) {
          setSharedState({
            businessStats: {
              totalContacts: statsResponse.data.contacts?.total ?? 0,
              messagesThisMonth: statsResponse.data.messages?.outbound ?? 0,
              teamCount: businessUsers.length,
            }
          });
        } else {
          setSharedState({ businessStats: { totalContacts: 0, messagesThisMonth: 0, teamCount: businessUsers.length } });
        }
      } catch (statsError) {
        console.log('⚠️ [useProfileCompletion] Could not fetch business stats:', statsError);
        setSharedState({ businessStats: null });
      }

      const channelServices: ServiceStatus[] = [];
      if (hasAiService) channelServices.push({
        name: 'AI assistance', code: 'ai', status: aiConfigured ? 'completed' : 'incomplete',
        icon: 'ai-bot', color: '#8B5CF6',
        description: aiConfigured ? 'AI model is connected' : 'Connect an AI model',
      });
      if (hasWhatsAppService) {
        channelServices.push({
          name: 'WhatsApp Business',
          code: 'wabu',
          status: whatsappConfigured ? 'completed' : 'incomplete',
          icon: 'logo-whatsapp',
          color: '#25D366',
          description: whatsappConfigured ? 'WhatsApp Business is configured' : 'WhatsApp Business needs configuration'
        });
      }
      if (hasFacebookService) {
        channelServices.push({
          name: 'Facebook Messenger',
          code: 'fbm',
          status: facebookConfigured ? 'completed' : 'incomplete',
          icon: 'logo-facebook',
          color: '#1877F2',
          description: facebookConfigured ? 'Facebook Messenger is configured' : 'Facebook Messenger needs configuration'
        });
      }
      if (hasXService) {
        channelServices.push({
          name: 'X (Twitter)',
          code: 'x_twitter',
          status: xConfigured ? 'completed' : 'incomplete',
          icon: 'logo-twitter',
          color: '#000000',
          description: xConfigured ? 'X (Twitter) is configured' : 'X (Twitter) needs configuration'
        });
      }
      if (hasPhoneService) {
        channelServices.push({
          name: 'Phone',
          code: 'phone',
          status: phoneConfigured ? 'completed' : 'incomplete',
          icon: 'call',
          color: '#0EA5E9',
          description: phoneConfigured ? 'Phone channel is configured' : 'Phone channel needs configuration'
        });
      }
      if (hasVisitsService) {
        // icon/color match getChannelIcon/getChannelColor's existing 'visit'
        // case (channelIcons.ts) — same badge Inbox/conversations already
        // use for this channel, kept consistent here.
        channelServices.push({
          name: 'Visits',
          code: 'visits',
          status: visitsConfigured ? 'completed' : 'incomplete',
          icon: 'location',
          color: '#F59E0B',
          description: visitsConfigured ? 'Visits is configured' : 'Visits needs configuration'
        });
      }

      const identitySteps: ServiceStatus[] = [];
      if (missingEmailIdentity) {
        identitySteps.push({
          name: 'Email Address',
          code: 'identity_email',
          status: 'incomplete',
          icon: 'mail-outline',
          color: '#6366F1',
          description: 'Add and verify an email address for account recovery and notifications'
        });
      }
      if (missingMobileIdentity) {
        identitySteps.push({
          name: 'Mobile Number',
          code: 'identity_mobile',
          status: 'incomplete',
          icon: 'call-outline',
          color: '#6366F1',
          description: 'Add and verify a mobile number for WhatsApp login and account recovery'
        });
      }

      const ownerServiceCount = businessRole === 'owner' ? 1 : 0;
      const completedChannelServices = channelServices.filter((s: any) => s.status === 'completed').length;
      const completedServices = completedChannelServices + (businessRole === 'owner' && userManagementConfigured ? 1 : 0);
      const totalServices = channelServices.length + ownerServiceCount + identitySteps.length;
      const percentage = totalServices > 0 ? Math.round((completedServices / totalServices) * 100) : 0;

      // Incomplete/not-started services surface first so the carousel opens
      // on what still needs attention, rather than a completed service.
      const STATUS_ORDER: Record<string, number> = { 'incomplete': 0, 'not-started': 0, 'completed': 1 };
      const allServicesUnsorted = [
        ...identitySteps,
        ...channelServices,
        ...(businessRole === 'owner'
          ? [{
              name: 'User Management',
              code: 'USER',
              status: (userManagementConfigured ? 'completed' : 'incomplete') as ServiceStatus['status'],
              icon: 'people-outline',
              color: '#007AFF',
              description: userManagementConfigured ? 'User Management is configured' : 'User Management needs configuration'
            }]
          : [])
      ];
      const sortedServices = [...allServicesUnsorted].sort(
        (a, b) => (STATUS_ORDER[a.status] ?? 0) - (STATUS_ORDER[b.status] ?? 0)
      );

      const completionData: ProfileCompletionData = {
        percentage,
        totalServices,
        completedServices,
        services: sortedServices
      };

      setSharedState({ profileCompletion: completionData });
      console.log('✅ [useProfileCompletion] Profile completion calculated from token:', completionData);

    } catch (error: any) {
      console.error('❌ [useProfileCompletion] Error calculating profile completion:', error);
      setSharedState({
        error: error.message || 'Failed to calculate profile completion',
        profileCompletion: DEFAULT_PROFILE_COMPLETION,
      });
    } finally {
      setSharedState({ isLoading: false });
    }
  })().finally(() => {
    fetchInFlight = null;
  });

  return fetchInFlight;
}

// Wires up the token-change subscription and the 2-minute auto-refresh
// exactly once for the whole app, on the first component that ever mounts
// this hook — not once per call site.
let started = false;
function ensureStarted() {
  if (started) return;
  started = true;

  fetchProfileCompletionShared();

  useAppStore.subscribe((state) => {
    const currentToken = state.tokens?.token;
    if (currentToken) {
      console.log('🔄 [useProfileCompletion] Token available, refreshing profile completion...');
      fetchProfileCompletionShared();
    }
  });

  setInterval(() => {
    // Only auto-refresh if we already have some data to avoid resetting during navigation
    if (sharedState.profileCompletion.services.length > 0) {
      console.log('🔄 [useProfileCompletion] Auto-refreshing profile completion...');
      fetchProfileCompletionShared();
    } else {
      console.log('🔄 [useProfileCompletion] Skipping auto-refresh - no services data yet');
    }
  }, 120000); // 2 minutes instead of 30 seconds
}

export const useProfileCompletion = () => {
  const [, forceRender] = useState(0);

  useEffect(() => {
    ensureStarted();
    const listener = () => forceRender(n => n + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return {
    ...sharedState,
    refresh: fetchProfileCompletionShared,
  };
};
